import React, { useState, useMemo, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Search, PackageOpen, ShoppingCart, Plus, Check, Package, Shuffle, ChevronLeft, ChevronRight } from 'lucide-react';
import { Product, OrderItem, CategoryMeta } from '../types';
import { CATEGORIES } from '../constants';
import SEO from '../components/SEO';

interface ShopProps {
  products: Product[];
  isVerified: boolean;
  onAddToCart: (product: Product, quantity: number) => void;
  cartItems: OrderItem[];
}

const CATEGORY_EMOJI: Record<string, string> = Object.fromEntries(
  CATEGORIES.map((c) => [c.name, c.emoji])
);

const PRODUCTS_PER_PAGE = 16;

const Shop: React.FC<ShopProps> = ({ products, isVerified, onAddToCart, cartItems }) => {
  const [activeCategory, setActiveCategory] = useState('All');
  const [activeSubcategory, setActiveSubcategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const resultsRef = useRef<HTMLElement>(null);

  const categoryNames = useMemo(() => {
    const cats = CATEGORIES.map((c) => c.name).filter((name) =>
      products.some((p) => p.category === name)
    );
    return ['All', ...cats];
  }, [products]);

  const activeCategoryMeta: CategoryMeta | undefined = useMemo(
    () => CATEGORIES.find((c) => c.name === activeCategory),
    [activeCategory]
  );

  // Category-filtered products (before subcategory filter)
  const filteredProducts = useMemo(() => {
    let result = products;
    if (activeCategory !== 'All') {
      result = result.filter((p) => p.category === activeCategory);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          p.description.toLowerCase().includes(q) ||
          p.sku.toLowerCase().includes(q) ||
          p.category.toLowerCase().includes(q)
      );
    }
    return result;
  }, [products, activeCategory, searchQuery]);

  // Subcategory names for chips
  const subcategoryNames = useMemo(() => {
    const subs = Array.from(new Set(filteredProducts.map((p) => p.subcategory || 'All Items')));
    return subs.length > 1 ? ['All', ...subs] : [];
  }, [filteredProducts]);

  // Apply subcategory filter
  const subcatFilteredProducts = useMemo(() => {
    if (activeSubcategory === 'All') return filteredProducts;
    return filteredProducts.filter((p) => (p.subcategory || 'All Items') === activeSubcategory);
  }, [filteredProducts, activeSubcategory]);

  // Pagination
  const totalPages = Math.ceil(subcatFilteredProducts.length / PRODUCTS_PER_PAGE);
  const paginatedProducts = useMemo(() => {
    const start = (currentPage - 1) * PRODUCTS_PER_PAGE;
    return subcatFilteredProducts.slice(start, start + PRODUCTS_PER_PAGE);
  }, [subcatFilteredProducts, currentPage]);

  // Reset helpers
  const handleCategoryChange = useCallback((cat: string) => {
    setActiveCategory(cat);
    setActiveSubcategory('All');
    setCurrentPage(1);
  }, []);

  const handleSubcategoryChange = useCallback((sub: string) => {
    setActiveSubcategory(sub);
    setCurrentPage(1);
  }, []);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setActiveSubcategory('All');
    setCurrentPage(1);
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
    resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const getCartQty = (productId: string) => {
    const item = cartItems.find(i => i.productId === productId);
    return item?.quantity || 0;
  };

  return (
    <div className="bg-brand-warm">
      <SEO
        title="Wholesale Product Catalogue"
        description="Browse 99+ wholesale multicultural products. Machetes, flag bandanas, keychains, caps, smudge sticks, and more. B2B pricing for approved retailers."
        path="/shop"
      />
      {/* Page Header */}
      <section className="bg-brand-dark">
        <div className="max-w-[1280px] mx-auto px-5 sm:px-10 py-10 sm:py-14">
          <div className="text-xs font-bold uppercase tracking-[2px] text-brand-red-light mb-3">Shop</div>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold text-white mb-3">Product Catalogue</h1>
          <p className="text-base text-white/60 max-w-[560px] leading-relaxed">
            Browse our wholesale multicultural product selection.
            {!isVerified && ' Sign in or apply for a wholesale account to view pricing and place orders.'}
          </p>
          <div className="flex items-center gap-4 mt-4 text-sm text-white/40">
            <span className="flex items-center gap-1.5"><Package className="w-4 h-4" /> Sold by the box/case only</span>
            <span className="hidden sm:inline text-white/20">|</span>
            <span className="hidden sm:flex items-center gap-1.5"><Shuffle className="w-4 h-4" /> Mix & match available in select categories</span>
          </div>
        </div>
      </section>

      {/* Category Tabs + Search */}
      <section className="bg-brand-light-bg border-b border-gray-100">
        <div className="max-w-[1280px] mx-auto px-5 sm:px-10 py-4">
          <div className="flex flex-col gap-3">
            {/* Scrollable category tabs */}
            <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
              {categoryNames.map((cat) => (
                <button
                  key={cat}
                  onClick={() => handleCategoryChange(cat)}
                  className={`px-4 py-2 rounded-[8px] text-sm font-medium transition-all whitespace-nowrap flex-shrink-0 ${
                    activeCategory === cat
                      ? 'bg-brand-red text-white shadow-sm'
                      : 'bg-white border border-gray-200 text-brand-dark hover:border-brand-red hover:text-brand-red'
                  }`}
                >
                  {cat !== 'All' && CATEGORY_EMOJI[cat] && (
                    <span className="mr-1.5">{CATEGORY_EMOJI[cat]}</span>
                  )}
                  {cat}
                </button>
              ))}
            </div>

            {/* Search + category info row */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              {/* Mix & match badge */}
              {activeCategoryMeta?.mixAndMatch && (
                <div className="flex items-center gap-2 bg-brand-green/10 text-brand-green rounded-lg px-3 py-2 text-[13px] font-medium">
                  <Shuffle className="w-4 h-4 flex-shrink-0" />
                  <span>{activeCategoryMeta.mixAndMatchNote || 'Mix & match available'}</span>
                </div>
              )}
              {activeCategory !== 'All' && activeCategoryMeta && !activeCategoryMeta.mixAndMatch && (
                <div className="flex items-center gap-2 bg-brand-earth/10 text-brand-earth rounded-lg px-3 py-2 text-[13px] font-medium">
                  <Package className="w-4 h-4 flex-shrink-0" />
                  <span>Sold by the case — no mix & match</span>
                </div>
              )}
              {activeCategory === 'All' && <div />}

              <div className="relative w-full sm:w-[280px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-text-muted" />
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-200 rounded-[8px] text-sm text-brand-dark placeholder:text-brand-text-muted/60 focus:ring-2 focus:ring-brand-red focus:border-brand-red outline-none transition-all"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Subcategory Chips */}
      {subcategoryNames.length > 0 && activeCategory !== 'All' && (
        <section className="bg-white border-b border-gray-100">
          <div className="max-w-[1280px] mx-auto px-5 sm:px-10 py-3">
            <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
              {subcategoryNames.map((sub) => (
                <button
                  key={sub}
                  onClick={() => handleSubcategoryChange(sub)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap flex-shrink-0 ${
                    activeSubcategory === sub
                      ? 'bg-brand-dark text-white'
                      : 'bg-gray-100 text-brand-text-muted hover:bg-gray-200 hover:text-brand-dark'
                  }`}
                >
                  {sub}
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Results */}
      <section ref={resultsRef} className="max-w-[1280px] mx-auto px-5 sm:px-10 py-8">
        <div className="text-sm text-brand-text-muted mb-5">
          Showing {paginatedProducts.length} of {subcatFilteredProducts.length} products
          {activeCategory !== 'All' && (
            <span> in <span className="font-semibold text-brand-dark">{activeCategory}</span></span>
          )}
          {activeSubcategory !== 'All' && (
            <span> &rsaquo; <span className="font-semibold text-brand-dark">{activeSubcategory}</span></span>
          )}
          {searchQuery.trim() && (
            <span> matching "<span className="font-semibold text-brand-dark">{searchQuery}</span>"</span>
          )}
          {totalPages > 1 && (
            <span className="ml-2 text-brand-text-muted/60">— page {currentPage} of {totalPages}</span>
          )}
        </div>

        {subcatFilteredProducts.length > 0 ? (
          <>
            {/* Flat product grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
              {paginatedProducts.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  isVerified={isVerified}
                  onAddToCart={onAddToCart}
                  cartQty={getCartQty(product.id)}
                  categoryMeta={CATEGORIES.find((c) => c.name === product.category)}
                  showCategory={activeCategory === 'All'}
                />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-8">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium bg-white border border-gray-200 text-brand-dark hover:border-brand-red hover:text-brand-red transition-all disabled:opacity-30 disabled:pointer-events-none"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Prev
                </button>

                {/* Page numbers */}
                <div className="flex items-center gap-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`w-9 h-9 rounded-lg text-sm font-medium transition-all ${
                        currentPage === page
                          ? 'bg-brand-red text-white shadow-sm'
                          : 'bg-white border border-gray-200 text-brand-dark hover:border-brand-red hover:text-brand-red'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                </div>

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium bg-white border border-gray-200 text-brand-dark hover:border-brand-red hover:text-brand-red transition-all disabled:opacity-30 disabled:pointer-events-none"
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-20">
            <PackageOpen className="w-16 h-16 text-brand-text-muted/30 mx-auto mb-4" />
            <h3 className="font-display font-bold text-xl text-brand-dark mb-2">No products found</h3>
            <p className="text-brand-text-muted text-sm">
              Try adjusting your search or filter to find what you're looking for.
            </p>
            <button
              onClick={() => { handleCategoryChange('All'); setSearchQuery(''); }}
              className="mt-4 text-sm font-semibold text-brand-red hover:text-brand-red-dark transition-colors"
            >
              Clear all filters
            </button>
          </div>
        )}
      </section>

      {/* B2B CTA (shown only if not verified) */}
      {!isVerified && (
        <section className="max-w-[1280px] mx-auto px-5 sm:px-10 pb-16">
          <div className="bg-brand-light-bg rounded-2xl p-8 sm:p-10 flex flex-col sm:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="font-display font-bold text-xl text-brand-dark mb-1">Want to see wholesale pricing?</h3>
              <p className="text-sm text-brand-text-muted">Apply for a wholesale account to unlock B2B prices and start ordering.</p>
            </div>
            <Link
              to="/apply"
              className="bg-brand-red text-white px-8 py-3.5 font-semibold text-[15px] rounded-[10px] hover:bg-brand-red-dark transition-all shadow-lg shadow-brand-red/25 hover:-translate-y-0.5 whitespace-nowrap"
            >
              Apply for Wholesale
            </Link>
          </div>
        </section>
      )}
    </div>
  );
};

/* ─── Product Card (Compact) ─────────────────────────────── */

interface ProductCardProps {
  product: Product;
  isVerified: boolean;
  onAddToCart: (product: Product, quantity: number) => void;
  cartQty: number;
  categoryMeta?: CategoryMeta;
  showCategory?: boolean;
}

const ProductCard: React.FC<ProductCardProps> = ({ product, isVerified, onAddToCart, cartQty, categoryMeta, showCategory }) => {
  const [imgError, setImgError] = useState(false);
  const [justAdded, setJustAdded] = useState(false);
  const emoji = CATEGORY_EMOJI[product.category] || '📦';
  const savingsPercent = Math.round(((product.msrp - product.price) / product.msrp) * 100);

  const handleAddToCart = () => {
    onAddToCart(product, product.boxQty);
    setJustAdded(true);
    setTimeout(() => setJustAdded(false), 1500);
  };

  const tagLabel = showCategory ? product.category : (product.subcategory || product.category);

  return (
    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden transition-all hover:shadow-lg hover:-translate-y-1 hover:border-brand-red group">
      {/* Image — square */}
      <div className="aspect-square bg-brand-light-bg flex items-center justify-center relative overflow-hidden">
        {!imgError ? (
          <img
            src={product.image}
            alt={product.name}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="flex flex-col items-center gap-1.5">
            <span className="text-4xl">{emoji}</span>
            <span className="text-[10px] text-brand-text-muted font-medium">{product.category}</span>
          </div>
        )}

        {/* Stock badge */}
        {product.stock > 0 && product.stock <= 10 ? (
          <div className="absolute top-2 right-2 bg-amber-50/95 backdrop-blur-sm rounded-full px-2 py-0.5 flex items-center gap-1 border border-amber-200">
            <div className="w-1.5 h-1.5 rounded-full bg-amber-500" />
            <span className="text-[10px] font-semibold text-amber-700 hidden sm:inline">Only {product.stock} left</span>
          </div>
        ) : product.stock > 0 ? (
          <div className="absolute top-2 right-2 bg-white/90 backdrop-blur-sm rounded-full px-2 py-0.5 flex items-center gap-1">
            <div className="w-1.5 h-1.5 rounded-full bg-brand-green" />
            <span className="text-[10px] font-semibold text-brand-dark hidden sm:inline">In Stock</span>
          </div>
        ) : (
          <div className="absolute top-2 right-2 bg-red-50/95 backdrop-blur-sm rounded-full px-2 py-0.5 flex items-center gap-1 border border-red-200">
            <div className="w-1.5 h-1.5 rounded-full bg-red-500" />
            <span className="text-[10px] font-semibold text-red-700 hidden sm:inline">Out of Stock</span>
          </div>
        )}

        {/* Cart qty badge */}
        {cartQty > 0 && (
          <div className="absolute top-2 left-2 bg-brand-red text-white rounded-full w-6 h-6 flex items-center justify-center">
            <span className="text-[10px] font-bold">{cartQty}</span>
          </div>
        )}

        {/* Mix & match badge */}
        {categoryMeta?.mixAndMatch && (
          <div className="absolute bottom-2 left-2 bg-brand-green/90 text-white rounded-full px-2 py-0.5 flex items-center gap-1 text-[9px] font-bold uppercase tracking-wide">
            <Shuffle className="w-2.5 h-2.5" />
            <span className="hidden sm:inline">Mix & Match</span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-3 sm:p-4">
        <div className="inline-block bg-brand-light-bg text-brand-text-muted text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded mb-2">
          {tagLabel}
        </div>

        <h3 className="font-display font-bold text-sm sm:text-[15px] text-brand-dark mb-0.5 leading-snug line-clamp-2">{product.name}</h3>
        <div className="text-[11px] text-brand-text-muted mb-1.5">SKU: {product.sku}</div>
        <p className="text-[12px] text-brand-text-muted leading-relaxed mb-2.5 line-clamp-1 hidden sm:block">{product.description}</p>

        {/* Box qty & MOQ info */}
        <div className="flex flex-wrap items-center gap-2 mb-3 text-[11px]">
          <div className="flex items-center gap-1 bg-brand-gold/10 text-brand-earth rounded px-1.5 py-0.5 font-semibold">
            <Package className="w-3 h-3" />
            <span className="hidden sm:inline">{product.unit}</span>
            <span className="sm:hidden">x{product.boxQty}</span>
          </div>
          <div className="text-brand-text-muted hidden sm:block">
            MOQ: <span className="font-semibold text-brand-dark">{product.moq}</span>
          </div>
        </div>

        {/* Price + Add to Cart */}
        {isVerified ? (
          <div className="pt-2.5 border-t border-gray-100">
            <div className="flex items-center justify-between mb-2">
              <div>
                <span className="text-lg font-bold text-brand-red">${product.price.toFixed(2)}</span>
                <span className="text-[10px] text-brand-text-muted ml-0.5">/ea</span>
              </div>
              <div className="bg-brand-green/10 text-brand-green text-[10px] font-bold px-1.5 py-0.5 rounded">
                {savingsPercent}%
              </div>
            </div>
            <div className="text-[11px] text-brand-text-muted line-through mb-2 hidden sm:block">MSRP ${product.msrp.toFixed(2)}</div>
            <button
              onClick={handleAddToCart}
              disabled={product.stock === 0}
              className={`w-full py-2 rounded-lg font-semibold text-xs sm:text-sm transition-all flex items-center justify-center gap-1.5 ${
                justAdded
                  ? 'bg-brand-green text-white'
                  : 'bg-brand-red text-white hover:bg-brand-red-dark shadow-sm hover:-translate-y-0.5'
              } disabled:opacity-40 disabled:cursor-not-allowed`}
            >
              {justAdded ? (
                <>
                  <Check className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Added {product.boxQty} pcs!</span>
                  <span className="sm:hidden">Added!</span>
                </>
              ) : (
                <>
                  <Plus className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Add {product.boxQty} pcs</span>
                  <span className="sm:hidden">Add to Cart</span>
                </>
              )}
            </button>
          </div>
        ) : (
          <div className="pt-2.5 border-t border-gray-100">
            <p className="text-[12px] text-brand-text-muted mb-2.5">Wholesale pricing for verified accounts only</p>
            <Link
              to="/apply"
              className="w-full py-2 rounded-lg font-semibold text-xs sm:text-sm bg-brand-red text-white hover:bg-brand-red-dark transition-all shadow-sm hover:-translate-y-0.5 flex items-center justify-center gap-1.5"
            >
              Apply for Account
            </Link>
            <Link
              to="/apply"
              className="w-full mt-1.5 text-[11px] text-brand-text-muted hover:text-brand-red transition-colors block text-center"
            >
              Already have an account? <span className="font-semibold">Sign in</span>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default Shop;
